import React, { useState, useEffect, useContext, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import apiInstance from '../../utils/axios';
import UserData from '../plugin/UserData';
import getCartID from '../plugin/cartID';
import GetCurrentAddress from '../plugin/UserCountry';
import Swal from 'sweetalert2';
import { CartContext } from '../../App';
import { useAuthStore } from '../../store/auth';
import PriceFormat_Basic from '../../components/commerce-ui/price-format-basic';
import PhoneInput from '../../components/ui/PhoneInput';
import InputIcon from '../../components/ui/InputIcon';
import CountrySelector from '../../components/ui/CountrySelector';
import { User, Mail, Phone, MapPin, Building, Map } from 'lucide-react';

const Toast = Swal.mixin({
  toast: true,
  position: 'top',
  showConfirmButton: false,
  timer: 3000,
  timerProgressBar: true,
});

// Format number with commas as thousands separator (US format: 6,000.00)
const formatCurrency = (value) => {
  if (value === null || value === undefined) return '0.00';
  const num = parseFloat(value);
  if (isNaN(num)) return '0.00';
  return num.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
};

function Cart() {
  const [cart, setCart] = useState([]);
  const [cartTotal, setCartTotal] = useState({});
  const [productQuantities, setProductQuantities] = useState({});
  const [loading, setLoading] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const hasFetched = useRef(false); // Track if we've already fetched

  // Performance Metrics State - REMOVED

  // Shipping Info
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [mobile, setMobile] = useState('');
  const [address, setAddress] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [country, setCountry] = useState('Paraguay');

  const [cartCount, setCartCount] = useContext(CartContext);

  const userData = UserData();
  const cart_id = getCartID();
  const currentAddress = GetCurrentAddress();
  const navigate = useNavigate();
  const isLoggedIn = useAuthStore((state) => state.isLoggedIn);

  // Safe helper function to get variant image
  const getVariantImage = (product, color) => {
    if (!product) return '';
    
    try {
              if (color && color !== 'Sin Color' && product.colors) {
        const colorObj = product.colors.find(c => c.name === color);
        if (colorObj && colorObj.image) {
          return colorObj.image;
        }
      }
      return product.image || '';
    } catch (error) {
      console.error('Error getting variant image:', error);
      return product.image || '';
    }
  };

  // Calculate performance metrics function - REMOVED

  // Step navigation
  const nextStep = () => {
    if (currentStep < 3) setCurrentStep(currentStep + 1);
  };

  const prevStep = () => {
    if (currentStep > 1) setCurrentStep(currentStep - 1);
  };

  const fetchCartData = async (cartId, userId) => {
    try {
      const url = userId ? `cart-list/${cartId}/${userId}/` : `cart-list/${cartId}/`;
      console.log('📦 Fetching cart data from:', url);
      const res = await apiInstance.get(url);
      
      console.log('📦 Raw cart response:', res.data);
      
      // The API returns { cart_items: [...], cart_total: {...} }
      // We need to access res.data.cart_items, not res.data directly
      const cartItems = res.data.cart_items || [];
      
      console.log('📦 Cart items found:', cartItems.length);
      
      // Check if cartItems is an array
      if (!Array.isArray(cartItems)) {
        console.error('❌ Cart items is not an array:', cartItems);
        console.error('❌ Full response data:', res.data);
        setCart([]);
        setCartCount(0);
        return;
      }
      
      // Filter out invalid cart items that don't have product data
      // Less restrictive - only remove items with critical issues
      const validCartItems = cartItems.filter(item => {
        if (!item || !item.product) {
          console.warn('⚠️ Invalid cart item found (no product):', item);
          return false;
        }
        
        console.log(`✅ Valid cart item: ${item.product.title} (Stock: ${item.product.stock_qty}, Qty: ${item.qty})`);
        return true;
      });
      
      console.log('📦 Valid cart items after filtering:', validCartItems.length);
      
      setCart(validCartItems);
      setCartCount(validCartItems.length);
      
      // Show warning if some items were filtered out
      if (validCartItems.length < cartItems.length) {
        const removedCount = cartItems.length - validCartItems.length;
        Toast.fire({
          icon: 'warning',
          title: `${removedCount} artículo(s) removido(s) del carrito (producto no disponible)`
        });
      }
      
      const quantities = {};
      validCartItems.forEach((item) => {
        quantities[item.id] = item.qty;
      });
      setProductQuantities(quantities);
    } catch (error) {
      console.error("❌ Error fetching cart:", error);
      console.error("❌ Error details:", error.response?.data);
      Toast.fire({
        icon: 'error',
        title: 'Error al cargar el carrito',
      });
      // Set empty cart on error to prevent crashes
      setCart([]);
      setCartCount(0);
    }
  };

  const fetchCartTotal = async (cartId, userId) => {
    try {
      const url = userId ? `cart-detail/${cartId}/${userId}/` : `cart-detail/${cartId}/`;
      const res = await apiInstance.get(url);
      setCartTotal(res.data);
      // Performance metrics calculation removed
    } catch (error) {
      console.error("Error fetching cart total:", error);
    }
  };

  useEffect(() => {
    // PREVENT MULTIPLE FETCHES - Only fetch once when component mounts
    if (hasFetched.current) {
      return;
    }
    
    if (!cart_id || userData === undefined) {
      return;
    }
    
    const userId = userData?.user_id || null;
    
    // Mark as fetched immediately
    hasFetched.current = true;
    
    // Simple fetch without complex logic
    const fetchData = async () => {
      try {
        setLoading(true);
        await fetchCartData(cart_id, userId);
        await fetchCartTotal(cart_id, userId);
        
        // Performance metrics calculation removed
        
      } catch (error) {
        console.error('Cart fetch error:', error);
        setCart([]);
        setCartCount(0);
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, []); // Empty dependency array - only run once

  // Performance metrics useEffect removed

  const handleQuantityChange = async (cartItemId, productId, price, shippingAmount, color, size, change) => {
    const cartItem = cart.find(item => item.id === cartItemId);
    if (!cartItem) {
      Toast.fire({
        icon: 'error',
        title: 'Error',
        text: 'Artículo del carrito no encontrado',
      });
      return;
    }

    if (!cartItem.product) {
      Toast.fire({
        icon: 'error',
        title: 'Error',
        text: 'Datos del producto faltantes',
      });
      return;
    }

    if (!cart_id) {
      Toast.fire({
        icon: 'error',
        title: 'Error',
        text: 'ID del carrito faltante',
      });
      return;
    }

    const currentQty = productQuantities[cartItemId] || cartItem.qty;
    const requestedQty = currentQty + change;
    
    // Show limit message if trying to exceed limits
    if (requestedQty > currentQty) {
      showCartLimitMessage(cartItem, requestedQty);
    }
    
    const validatedQty = validateQuantity(cartItem, requestedQty);

    // If quantity didn't change after validation, don't proceed
    if (validatedQty === currentQty) return;

    // Update local state immediately
    setProductQuantities(prev => ({
      ...prev,
      [cartItemId]: validatedQty
    }));

    try {
      // Use the correct API endpoint for updating cart quantities
      const updateData = {
        qty: validatedQty
      };

      await apiInstance.patch(`cart-update/${cart_id}/${cartItemId}/`, updateData);
      
      // Refresh cart data and totals
      await fetchCartData(cart_id, userData?.user_id);
      await fetchCartTotal(cart_id, userData?.user_id);
      // Performance metrics calculation removed
      
      Toast.fire({
        icon: 'success',
        title: 'Actualizado',
        text: 'Cantidad actualizada exitosamente',
      });
    } catch (error) {
      console.error("Update Error:", error);
      console.error("Error details:", error.response?.data);
      // Revert local state on error
      setProductQuantities(prev => ({
        ...prev,
        [cartItemId]: currentQty
      }));
      
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'No se pudo actualizar la cantidad',
      });
    }
  };

  const handleInputChange = async (e, cartItemId, productId, price, shippingAmount, color, size) => {
    const cartItem = cart.find(item => item.id === cartItemId);
    if (!cartItem) {
      Toast.fire({
        icon: 'error',
        title: 'Error',
        text: 'Cart item not found',
      });
      return;
    }

    if (!cartItem.product) {
      Toast.fire({
        icon: 'error',
        title: 'Error',
        text: 'Product data missing',
      });
      return;
    }

    if (!cart_id) {
      Toast.fire({
        icon: 'error',
        title: 'Error',
        text: 'ID del carrito faltante',
      });
      return;
    }

    const requestedQty = parseInt(e.target.value) || 1;
    
    // Show limit message if trying to exceed limits
    if (requestedQty > (productQuantities[cartItemId] || cartItem.qty)) {
      showCartLimitMessage(cartItem, requestedQty);
    }
    
    const validatedQty = validateQuantity(cartItem, requestedQty);

    setProductQuantities(prev => ({
      ...prev,
      [cartItemId]: validatedQty
    }));

    try {
      // Use the correct API endpoint for updating cart quantities
      const updateData = {
        qty: validatedQty
      };

      await apiInstance.patch(`cart-update/${cart_id}/${cartItemId}/`, updateData);
      
      // Refresh cart data and totals
      await fetchCartData(cart_id, userData?.user_id);
      await fetchCartTotal(cart_id, userData?.user_id);
              // Performance metrics calculation removed
    } catch (error) {
      console.error("Input Change Update Error:", error);
      console.error("Input Change Error details:", error.response?.data);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'No se pudo actualizar la cantidad',
      });
    }
  };

  const validateQuantity = (cartItem, newQty) => {
    // SIMPLIFIED VALIDATION - Basic checks only
    if (!cartItem || !cartItem.product) {
      return 1;
    }
    
    const minQty = 1;
    const maxQty = Math.min(cartItem.product.max_cart_limit || 10, cartItem.product.stock_qty || 1);
    
    if (newQty < minQty) return minQty;
    if (newQty > maxQty) return maxQty;
    
    return newQty;
  };

  // Function to show cart limit message
  const showCartLimitMessage = (cartItem, requestedQty) => {
    const maxCartLimit = cartItem.product.max_cart_limit || 10;
    const currentStock = cartItem.product.stock_qty || 0;
    const maxAllowed = Math.min(maxCartLimit, currentStock);
    
    if (requestedQty > maxCartLimit) {
      Toast.fire({
        icon: 'warning',
        title: 'Límite del Carrito Alcanzado',
        text: `Máximo ${maxCartLimit} artículos permitidos por carrito para este producto.`,
      });
    } else if (requestedQty > currentStock) {
      Toast.fire({
        icon: 'warning',
        title: 'Stock Insuficiente',
                        text: `Solo ${currentStock} artículos disponibles en stock.`,
      });
    }
  };

  const handleDeleteCartItem = async (cartId, itemId) => {
    try {
      const url = userData?.user_id
        ? `cart-delete/${cartId}/${itemId}/${userData.user_id}/`
        : `cart-delete/${cartId}/${itemId}/`;

      const response = await apiInstance.delete(url);
      
      const updatedCart = cart.filter(item => item.id !== itemId);
      
      setCart(updatedCart);
      setCartCount(updatedCart.length);
      fetchCartTotal(cart_id, userData?.user_id);

      Toast.fire({
        icon: 'success',
        title: 'Eliminado del Carrito'
      });
    } catch (error) {
      console.error('Deletion Error:', error);
      console.error('Error details:', error.response?.data);
      Swal.fire({
        icon: 'error',
        title: 'error',
        text: 'No se pudo eliminar el producto'
      });
    }
  };

  const createOrder = async () => {
    // Check if user is logged in
    if (!isLoggedIn()) {
      Swal.fire({
        icon: 'warning',
        title: 'Inicio de Sesión Requerido',
        text: 'Por favor inicia sesión para continuar',
      });
      return;
    }

    if (!fullName || !email || !mobile || !address || !city || !state || !country) {
      Swal.fire({
        icon: 'warning',
        title: 'Campos Requeridos',
        text: 'Por favor completa todos los campos',
      });
      return;
    }

    // Validate stock before creating order
    const stockValidation = await validateCartStock();
    if (!stockValidation.valid) {
      Swal.fire({
        icon: 'error',
        title: 'Stock Insuficiente',
        text: stockValidation.message,
      });
      return;
    }

    try {
      setLoading(true);
      const orderData = {
        full_name: fullName,
        email: email,
        phone: mobile,
        address: address,
        city: city,
        state: state,
        country: country,
        cart_id: cart_id,
        user_id: userData ? userData.user_id : 0
      };

      const response = await apiInstance.post('create-order/', orderData);
      
      if (response.data?.order_oid) {
        setCart([]);
        setCartCount(0);
        Toast.fire({
          icon: 'success',
          title: 'Pedido Realizado'
        });
        navigate(`/checkout/${response.data.order_oid}`);
      } else {
        throw new Error('No order ID returned');
      }
    } catch (error) {
      console.error('Order Error:', error.response?.data || error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error.response?.data?.error || error.message || 'No se pudo crear el pedido',
      });
    } finally {
      setLoading(false);
    }
  };

  const validateCartStock = async () => {
    try {
      // Re-fetch current product data to check stock
      for (const item of cart) {
        const productResponse = await apiInstance.get(`products/${item.product.id}/`);
        const currentStock = productResponse.data.stock_qty;
        const requestedQty = productQuantities[item.id] || item.qty;

        if (currentStock <= 0) {
          return {
            valid: false,
            message: `El producto ${item.product.title} ya no está disponible en stock`
          };
        }

        if (requestedQty > currentStock) {
          return {
            valid: false,
            message: `Solo ${currentStock} unidades del producto ${item.product.title} están disponibles`
          };
        }
      }
      return { valid: true };
    } catch (error) {
      console.error('Stock validation error:', error);
      return {
        valid: false,
        message: 'Error al validar el stock'
      };
    }
  };

  const sendWhatsAppMessage = () => {
    // Create formatted message with cart items
    let message = `🛒 *Pedido de Herramientas Tayrun*\n\n`;
    message += `👤 *Cliente:* ${fullName}\n`;
    message += `📞 *Telefone:* ${mobile}\n`;
    message += `📧 *Email:* ${email}\n\n`;
    message += `📦 *Produtos:*\n`;
    
    cart.forEach((item, index) => {
              const colorText = item.color !== 'Sin Color' ? ` cor ${item.color}` : '';
              const sizeText = item.size !== 'Sin Talla' ? ` tamanho ${item.size}` : '';
      message += `${index + 1}. ${item.qty} ${item.product.title}${colorText}${sizeText}\n`;
    });
    
    message += `\n💰 *Total:* $${formatCurrency(cartTotal.total)}\n\n`;
    message += `📍 *Endereço de Entrega:*\n${address}, ${city}, ${state}, ${country}\n\n`;
    message += `Gostaria de finalizar este pedido!`;
    
    // Encode message for URL
    const encodedMessage = encodeURIComponent(message);
    const whatsappUrl = `https://wa.me/5545999456009?text=${encodedMessage}`;
    
    // Open WhatsApp in new tab
    window.open(whatsappUrl, '_blank');
  };

  // Enhanced WhatsApp checkout that creates CartOrder
  const sendWhatsAppMessageWithOrder = async () => {
    // Validate required fields
    if (!fullName || !email || !mobile || !address || !city || !state || !country) {
      Toast.fire({
        icon: 'warning',
        title: 'Campos Requeridos Faltantes',
        text: 'Por favor complete todos los campos requeridos antes de proceder con el checkout de WhatsApp.'
      });
      return;
    }

    // Validate cart is not empty
    if (!cart || cart.length === 0) {
      Toast.fire({
        icon: 'warning',
                        title: 'Carrito Vacío',
                        text: 'Tu carrito está vacío. Por favor agrega artículos antes de proceder con el checkout.'
      });
      return;
    }

    // Validate cart items have required data
    for (const item of cart) {
      if (!item.product || !item.product.id || !item.product.price) {
        Toast.fire({
          icon: 'error',
          title: 'Artículo del Carrito Inválido',
          text: 'Uno o más artículos del carrito son inválidos. Por favor actualiza tu carrito e intenta nuevamente.'
        });
        return;
      }
      
      if (!item.qty || item.qty <= 0) {
        Toast.fire({
          icon: 'error',
          title: 'Cantidad Inválida',
          text: `Cantidad inválida para ${item.product.title}. Por favor actualiza tu carrito e intenta nuevamente.`
        });
        return;
      }
    }

    try {
      setLoading(true);
      // Create the order in the database using the existing create-order endpoint
      const orderData = {
        full_name: fullName,
        email: email,
        phone: mobile,
        address: address,
        city: city,
        state: state,
        country: country,
        cart_id: cart_id,
        user_id: userData ? userData.user_id : 0,
        payment_method: 'whatsapp' // This will prevent immediate stock reduction
      };

      // Use the existing create-order API endpoint
      const response = await apiInstance.post('create-order/', orderData);
      
      if (!response.data?.order_oid) {
        throw new Error('No order ID returned from server');
      }

      // Create clean WhatsApp message with invoice-style formatting (no emojis)
      const orderDate = new Date().toLocaleDateString('es-PY', { 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
      
      // Generate invoice URL
      const invoiceUrl = `${window.location.origin}/customer/invoice/${response.data.order_oid}/`;
      
      let message = `═══════════════════════════\n`;
      message += `    *HERRAMIENTAS TAYRUN*\n`;
      message += `═══════════════════════════\n\n`;
      message += `• *PEDIDO #${response.data.order_oid}*\n`;
      message += `• Fecha: ${orderDate}\n\n`;
      message += `───────────────────────────\n`;
      message += `    *DATOS DEL CLIENTE*\n`;
      message += `───────────────────────────\n`;
      message += `• Nombre: ${fullName}\n`;
      message += `• Teléfono: ${mobile}\n`;
      message += `• Email: ${email}\n\n`;
      message += `───────────────────────────\n`;
      message += `    *DIRECCIÓN DE ENTREGA*\n`;
      message += `───────────────────────────\n`;
      message += `${address}\n`;
      message += `${city}, ${state}\n`;
      message += `${country}\n\n`;
      message += `───────────────────────────\n`;
      message += `    *PRODUCTOS DEL PEDIDO*\n`;
      message += `───────────────────────────\n\n`;
      
      cart.forEach((item, index) => {
        const colorText = item.color && item.color !== 'Sin Color' && item.color !== 'No Color' ? `\n   • Color: ${item.color}` : '';
        const sizeText = item.size && item.size !== 'Sin Talla' && item.size !== 'No Size' ? `\n   • Talla: ${item.size}` : '';
        const itemTotal = formatCurrency(item.qty * parseFloat(item.price || item.product.price));
        message += `*${index + 1}. ${item.product.title}*\n`;
        message += `   • Cantidad: ${item.qty}\n`;
        message += `   • Precio: $${formatCurrency(item.price || item.product.price)}\n`;
        message += `   • Subtotal: $${itemTotal}${colorText}${sizeText}\n\n`;
      });
      
      message += `───────────────────────────\n`;
      message += `    *RESUMEN DEL PEDIDO*\n`;
      message += `───────────────────────────\n`;
      message += `   • Subtotal: $${formatCurrency(cartTotal.sub_total)}\n`;
      if (cartTotal.shipping > 0) {
        message += `   • Envío: $${formatCurrency(cartTotal.shipping)}\n`;
      }
      if (cartTotal.tax > 0) {
        message += `   • Impuesto: $${formatCurrency(cartTotal.tax)}\n`;
      }
      message += `\n`;
      message += `═══════════════════════════\n`;
      message += `   *TOTAL: $${formatCurrency(cartTotal.total)}*\n`;
      message += `═══════════════════════════\n\n`;
      message += `*Gracias por su pedido!*\n`;
      message += `Responda este mensaje para confirmar.\n\n`;
      message += `Ver factura completa:\n${invoiceUrl}\n\n`;
      message += `_Herramientas Tayrun - Su tienda de confianza_`;
      
      // Encode message for URL
      const encodedMessage = encodeURIComponent(message);
      const whatsappUrl = `https://wa.me/5545999456009?text=${encodedMessage}`;
      
      // Open WhatsApp in new tab
      window.open(whatsappUrl, '_blank');
      
      // Clear the cart state directly instead of deleting items one by one
      // This avoids potential errors with cart deletion after order creation
      setCart([]);
      setCartCount(0);
      
      // Show enhanced success message
      Swal.fire({
        icon: 'success',
        title: '¡Pedido de WhatsApp Creado Exitosamente! 🎉',
        html: `
          <div style="text-align: left;">
            <p><strong>ID del Pedido:</strong> ${response.data.order_oid}</p>
            <p><strong>Estado:</strong> Pago Pendiente</p>
            <p><strong>Nota:</strong> El stock está reservado pero no se reduce hasta que se confirme el pago.</p>
          </div>
        `,
                        confirmButtonText: 'Abrir WhatsApp',
        showCancelButton: true,
                        cancelButtonText: 'Ir al Inicio',
        confirmButtonColor: '#25D366'
      }).then((result) => {
        if (result.isConfirmed) {
          // Open WhatsApp in new tab
          window.open(whatsappUrl, '_blank');
        }
        // Always redirect to home page after WhatsApp order
        navigate('/');
      });
      
      // Update cart count
      setCartCount(0);
    } catch (error) {
      console.error('Error creating WhatsApp order:', error);
      
      let errorMessage = 'Error al crear pedido de WhatsApp. Por favor intenta nuevamente.';
      if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      }
      
      Swal.fire({
        icon: 'error',
        title: 'Creación de Pedido Fallida',
        text: errorMessage,
        confirmButtonText: 'OK'
      });
    } finally {
      setLoading(false);
    }
  };

  const renderCartItems = () => (
    <div className="cart-items" style={{ padding: '32px' }}>
      {loading ? (
        <div style={{ textAlign: 'center', padding: '60px 0' }}>
          <div style={{
            width: '48px',
            height: '48px',
            border: '4px solid #f3f4f6',
            borderTop: '4px solid #3b82f6',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 20px'
          }}></div>
          <span style={{ color: '#6b7280', fontSize: '16px' }}>Cargando...</span>
        </div>
      ) : !cart || cart.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '80px 20px' }}>
          <div style={{
            width: '120px',
            height: '120px',
            margin: '0 auto 24px',
            backgroundColor: '#f3f4f6',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <i className="fas fa-shopping-cart" style={{ fontSize: '54px', color: '#d1d5db' }}></i>
          </div>
                          <h3 style={{ color: '#1f2937', marginBottom: '12px', fontSize: '24px', fontWeight: '600' }}>Tu carrito está vacío</h3>
                          <p style={{ color: '#6b7280', marginBottom: '32px', fontSize: '16px', maxWidth: '400px', margin: '0 auto 32px' }}>Agrega algunos productos para comenzar</p>
          <Link to="/products" style={{
            backgroundColor: '#3b82f6',
            color: 'white',
            padding: '14px 40px',
            borderRadius: '8px',
            textDecoration: 'none',
            display: 'inline-block',
            fontWeight: '600',
            fontSize: '16px',
            transition: 'all 0.2s ease',
            boxShadow: '0 4px 6px -1px rgba(59, 130, 246, 0.25)'
          }}
          onMouseEnter={(e) => {
            e.target.style.backgroundColor = '#2563eb';
            e.target.style.transform = 'translateY(-2px)';
            e.target.style.boxShadow = '0 6px 12px -1px rgba(59, 130, 246, 0.35)';
          }}
          onMouseLeave={(e) => {
            e.target.style.backgroundColor = '#3b82f6';
            e.target.style.transform = 'translateY(0)';
            e.target.style.boxShadow = '0 4px 6px -1px rgba(59, 130, 246, 0.25)';
          }}>
            Continuar Comprando
          </Link>
        </div>
      ) : (
        <div>
          <div style={{ 
            marginBottom: '28px', 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between' 
          }}>
            <h3 style={{ 
              margin: 0, 
              color: '#1f2937', 
              fontSize: '22px',
              fontWeight: '600' 
            }}>
              Artículos del Carrito 
              <span style={{ 
                color: '#6b7280', 
                fontSize: '18px', 
                fontWeight: '400',
                marginLeft: '8px' 
              }}>
                ({cart.length})
              </span>
            </h3>
          </div>
          {cart.map((item, index) => {
            if (!item || !item.product) {
              return (
                <div key={`invalid-${index}`} style={{
                  padding: '15px',
                  border: '1px solid #ef4444',
                  borderRadius: '8px',
                  marginBottom: '10px',
                  backgroundColor: '#fef2f2',
                  color: '#dc2626'
                }}>
                  <p><strong>Artículo Inválido</strong></p>
                  <button
                    onClick={() => handleDeleteCartItem(cart_id, item?.id)}
                    style={{
                      backgroundColor: '#dc2626',
                      color: 'white',
                      border: 'none',
                      padding: '8px 16px',
                      borderRadius: '6px',
                      cursor: 'pointer'
                    }}
                  >
                    Eliminar
                  </button>
                </div>
              );
            }

            return (
              <div key={item.id} className="cart-item" style={{
                display: 'flex',
                gap: '24px',
                padding: '24px',
                border: '1px solid #e5e7eb',
                borderRadius: '16px',
                marginBottom: '20px',
                backgroundColor: 'white',
                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.06)',
                transition: 'all 0.2s ease',
                position: 'relative'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.08)';
                e.currentTarget.style.transform = 'translateY(-2px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.06)';
                e.currentTarget.style.transform = 'translateY(0)';
              }}>
                {/* Product Image */}
                <div style={{ flexShrink: 0 }}>
                  <div style={{
                    width: '140px',
                    height: '140px',
                    borderRadius: '12px',
                    overflow: 'hidden',
                    backgroundColor: '#f9fafb',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: '1px solid #e5e7eb'
                  }}>
                    <img
                      src={getVariantImage(item.product, item.color)}
                      alt={item.product.title || 'Product'}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'contain',
                        padding: '12px'
                      }}
                      onError={(e) => {
                        e.target.src = item.product.image || '';
                      }}
                    />
                  </div>
                </div>
                
                {/* Product Details */}
                <div className="cart-item-content" style={{ flex: 1 }}>
                  <h5 style={{ 
                    fontSize: '20px', 
                    fontWeight: '600', 
                    color: '#1f2937',
                    marginBottom: '12px',
                    lineHeight: '1.3'
                  }}>
                    {item.product.title || 'Unknown Product'}
                  </h5>
                  
                  {/* Only Size and Color below title */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginBottom: '16px' }}>
                    {item.size && item.size !== 'Sin Talla' && (
                      <span style={{ fontSize: '14px', color: '#6b7280' }}>
                        <strong>Talla:</strong> {item.size}
                      </span>
                    )}
                    {item.color && item.color !== 'Sin Color' && (
                      <span style={{ fontSize: '14px', color: '#6b7280' }}>
                        <strong>Color:</strong> {item.color}
                      </span>
                    )}
                  </div>
                  
                  {/* Additional Info - Price, Stock, Cart Limit */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginBottom: '16px' }}>
                    <span style={{ fontSize: '14px', color: '#6b7280' }}>
                      <strong>Precio:</strong> <PriceFormat_Basic value={item.price || 0} />
                    </span>
                    <span style={{ 
                      fontSize: '14px', 
                      color: (item.product.stock_qty || 0) > 10 ? '#059669' : (item.product.stock_qty || 0) > 0 ? '#f59e0b' : '#dc2626',
                      fontWeight: '500'
                    }}>
                      <strong>Stock:</strong> {
                        (item.product.stock_qty || 0) > 10 ? `${item.product.stock_qty} disponible` :
                        (item.product.stock_qty || 0) > 0 ? `¡Solo ${item.product.stock_qty} disponible!` :
                        'Agotado'
                      }
                    </span>
                    {item.product.max_cart_limit && (
                      <span style={{ 
                        fontSize: '14px', 
                        color: '#3b82f6',
                        fontWeight: '500'
                      }}>
                        <strong>Límite del Carrito:</strong> Máximo {item.product.max_cart_limit} por carrito
                      </span>
                    )}
                  </div>
                  
                  {/* Remove Button */}
                  <button
                    onClick={() => handleDeleteCartItem(cart_id, item.id)}
                    style={{
                      backgroundColor: 'transparent',
                      border: '1px solid #ef4444',
                      color: '#ef4444',
                      padding: '8px 16px',
                      borderRadius: '6px',
                      fontSize: '14px',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.backgroundColor = '#ef4444';
                      e.target.style.color = 'white';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = 'transparent';
                      e.target.style.color = '#ef4444';
                    }}
                  >
                    <i className="fas fa-trash me-1"></i> Eliminar
                  </button>
                </div>
                
                {/* Right Side - Quantity Controls & Total */}
                <div className="cart-item-actions" style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '16px' }}>
                  {/* Item Total */}
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ 
                      fontSize: '12px', 
                      color: '#9ca3af', 
                      marginBottom: '4px', 
                      textTransform: 'uppercase', 
                      letterSpacing: '0.05em',
                      fontWeight: '500'
                    }}>
                      Total
                    </div>
                    <h6 style={{ 
                      fontSize: '24px', 
                      fontWeight: '700', 
                      color: '#1f2937',
                      margin: 0
                    }}>
                      <PriceFormat_Basic value={(item.price || 0) * (productQuantities[item.id] || item.qty || 1)} />
                    </h6>
                  </div>
                  
                  {/* Quantity Controls */}
                  <div className="quantity-controls" style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '12px',
                    backgroundColor: '#f9fafb',
                    padding: '8px',
                    borderRadius: '8px',
                    border: '1px solid #e5e7eb'
                  }}>
                    <button 
                      onClick={() => handleQuantityChange(
                        item.id,
                        item.product.id,
                        item.product.price || 0,
                        item.product.shipping_amount || 0,
                        item.color,
                        item.size,
                        -1
                      )}
                      disabled={(item.product.stock_qty || 0) <= 0 || (productQuantities[item.id] || item.qty || 1) <= 1}
                      style={{
                        width: '36px',
                        height: '36px',
                        border: 'none',
                        backgroundColor: (item.product.stock_qty || 0) <= 0 || (productQuantities[item.id] || item.qty || 1) <= 1 ? '#e5e7eb' : 'white',
                        borderRadius: '6px',
                        cursor: (item.product.stock_qty || 0) > 0 && (productQuantities[item.id] || item.qty || 1) > 1 ? 'pointer' : 'not-allowed',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        opacity: (item.product.stock_qty || 0) > 0 && (productQuantities[item.id] || item.qty || 1) > 1 ? 1 : 0.5,
                        transition: 'all 0.2s ease',
                        boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)'
                      }}
                      onMouseEnter={(e) => {
                        if ((item.product.stock_qty || 0) > 0 && (productQuantities[item.id] || item.qty || 1) > 1) {
                          e.target.style.backgroundColor = '#f3f4f6';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if ((item.product.stock_qty || 0) > 0 && (productQuantities[item.id] || item.qty || 1) > 1) {
                          e.target.style.backgroundColor = 'white';
                        }
                      }}
                    >
                      <i className="fas fa-minus" style={{ fontSize: '14px', color: '#374151' }}></i>
                    </button>
                    
                    <input
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      value={productQuantities[item.id] || item.qty || 1}
                      min={1}
                      max={Math.min(item.product.max_cart_limit || 10, item.product.stock_qty || 1)}
                      disabled={(item.product.stock_qty || 0) <= 0}
                      onChange={(e) => handleInputChange(
                        e,
                        item.id,
                        item.product.id,
                        item.product.price || 0,
                        item.product.shipping_amount || 0,
                        item.color,
                        item.size
                      )}
                      style={{
                        width: '70px',
                        height: '36px',
                        textAlign: 'center',
                        border: (productQuantities[item.id] || item.qty || 1) >= Math.min(item.product.max_cart_limit || 10, item.product.stock_qty || 1) 
                          ? '2px solid #f59e0b' 
                          : '1px solid #e5e7eb',
                        borderRadius: '6px',
                        fontSize: '16px',
                        fontWeight: '600',
                        color: '#1f2937',
                        opacity: (item.product.stock_qty || 0) > 0 ? 1 : 0.5,
                        backgroundColor: (productQuantities[item.id] || item.qty || 1) >= Math.min(item.product.max_cart_limit || 10, item.product.stock_qty || 1)
                          ? '#fef3c7'
                          : 'white'
                      }}
                    />
                    
                    <button 
                      onClick={() => handleQuantityChange(
                        item.id,
                        item.product.id,
                        item.product.price || 0,
                        item.product.shipping_amount || 0,
                        item.color,
                        item.size,
                        1
                      )}
                      disabled={(item.product.stock_qty || 0) <= 0 || (productQuantities[item.id] || item.qty || 1) >= Math.min(item.product.max_cart_limit || 10, item.product.stock_qty || 1)}
                      style={{
                        width: '36px',
                        height: '36px',
                        border: 'none',
                        backgroundColor: (productQuantities[item.id] || item.qty || 1) >= Math.min(item.product.max_cart_limit || 10, item.product.stock_qty || 1)
                          ? '#fef3c7'
                          : 'white',
                        borderRadius: '6px',
                        cursor: (item.product.stock_qty || 0) > 0 && (productQuantities[item.id] || item.qty || 1) < Math.min(item.product.max_cart_limit || 10, item.product.stock_qty || 1) ? 'pointer' : 'not-allowed',
                        display: 'flex',
                        transition: 'all 0.2s ease',
                        boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
                        alignItems: 'center',
                        justifyContent: 'center',
                        opacity: (item.product.stock_qty || 0) > 0 ? 1 : 0.5
                      }}
                      onMouseEnter={(e) => {
                        const isDisabled = (item.product.stock_qty || 0) <= 0 || (productQuantities[item.id] || item.qty || 1) >= Math.min(item.product.max_cart_limit || 10, item.product.stock_qty || 1);
                        if (!isDisabled) {
                          e.currentTarget.style.backgroundColor = '#f3f4f6';
                        }
                      }}
                      onMouseLeave={(e) => {
                        const isDisabled = (item.product.stock_qty || 0) <= 0 || (productQuantities[item.id] || item.qty || 1) >= Math.min(item.product.max_cart_limit || 10, item.product.stock_qty || 1);
                        if (!isDisabled) {
                          e.currentTarget.style.backgroundColor = 'white';
                        }
                      }}
                    >
                      <i className="fas fa-plus" style={{ fontSize: '14px', color: '#374151' }}></i>
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );

  return (
    <div style={{ backgroundColor: '#f8fafc', minHeight: '100vh', paddingTop: '40px', paddingBottom: '40px', overflowX: 'hidden' }}>
      <div className="container" style={{ maxWidth: '1100px', overflowX: 'hidden', margin: '0 auto' }}>
        {/* Performance Metrics Section - REMOVED */}
          
          {/* No Data Message - REMOVED */}

        {/* Progress Steps */}
        <div style={{ marginBottom: '40px' }}>
          <div className="step-navigation" style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: '20px',
            backgroundColor: 'white',
            padding: '20px',
            borderRadius: '12px',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
          }}>
            {[
              { step: 1, icon: 'fas fa-shopping-cart', label: 'Carrito de Compras' },
              { step: 2, icon: 'fas fa-truck', label: 'Envío' },
              { step: 3, icon: 'fas fa-credit-card', label: 'Pago' }
            ].map((item, index) => (
              <React.Fragment key={item.step}>
                <button 
                  onClick={() => item.step <= currentStep && setCurrentStep(item.step)}
                  disabled={cart.length === 0 && item.step > 1}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '12px 20px',
                    border: 'none',
                    borderRadius: '8px',
                    backgroundColor: currentStep === item.step ? '#1f2937' : 'transparent',
                    color: currentStep === item.step ? 'white' : '#6b7280',
                    cursor: (cart.length === 0 && item.step > 1) ? 'not-allowed' : 'pointer',
                    fontSize: '16px',
                    fontWeight: '600',
                    transition: 'all 0.2s ease',
                    opacity: (cart.length === 0 && item.step > 1) ? 0.5 : 1
                  }}
                >
                  <i className={item.icon}></i> {item.label}
                </button>
                {index < 2 && (
                  <div style={{
                    width: '40px',
                    height: '2px',
                    backgroundColor: currentStep > item.step ? '#1f2937' : '#e5e7eb'
                  }}></div>
                )}
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* Step 1: Cart */}
        {currentStep === 1 && (
          <div className="cart-layout" style={{
            display: 'grid',
            gridTemplateColumns: '1fr 350px',
            gap: '30px',
            alignItems: 'start'
          }}>
            <div className="cart-main" style={{
              minWidth: 0, // Prevent grid overflow
              '@media (max-width: 1024px)': {
                gridColumn: '1 / -1'
              }
            }}>
              <div style={{
                backgroundColor: 'white',
                borderRadius: '16px',
                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                marginBottom: '24px',
                overflow: 'hidden'
              }}>
                <div style={{
                  padding: '32px',
                  borderBottom: '1px solid #e5e7eb',
                  backgroundColor: '#f9fafb',
                  borderRadius: '16px 16px 0 0'
                }}>
                  <h4 style={{
                    margin: 0,
                    fontSize: '24px',
                    fontWeight: 'bold',
                    color: '#1f2937',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px'
                  }}>
                    <i className="fas fa-shopping-cart" style={{ color: '#3b82f6' }}></i>
                    Carrito de Compras ({cartCount} productos)
                  </h4>
                </div>
                {renderCartItems()}
              </div>
            </div>

            {cart?.length > 0 && (
              <div className="cart-sidebar" style={{
                position: 'sticky',
                top: '20px',
                height: 'fit-content'
              }}>
                <div style={{
                  backgroundColor: 'white',
                  borderRadius: '16px',
                  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                  overflow: 'hidden'
                }}>
                  <div style={{
                    padding: '32px',
                    borderBottom: '1px solid #e5e7eb',
                    backgroundColor: '#f9fafb',
                    borderRadius: '16px 16px 0 0'
                  }}>
                    <h5 style={{
                      margin: 0,
                      fontSize: '20px',
                      fontWeight: 'bold',
                      color: '#1f2937',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}>
                      <i className="fas fa-receipt" style={{ color: '#10b981' }}></i>
                      Resumen del Pedido
                    </h5>
                  </div>
                  <div style={{ padding: '32px' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', marginBottom: '24px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ color: '#6b7280', fontSize: '15px' }}>Subtotal</span>
                        <span style={{ fontWeight: '600', fontSize: '16px' }}>${formatCurrency(cartTotal.sub_total)}</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ color: '#6b7280', fontSize: '15px' }}>Envío</span>
                        <span style={{ fontWeight: '600', fontSize: '16px' }}>${formatCurrency(cartTotal.shipping)}</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ color: '#6b7280', fontSize: '15px' }}>Impuesto</span>
                        <span style={{ fontWeight: '600', fontSize: '16px' }}>${formatCurrency(cartTotal.tax)}</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ color: '#6b7280', fontSize: '15px' }}>Tarifa de Servicio</span>
                        <span style={{ fontWeight: '600', fontSize: '16px' }}>${formatCurrency(cartTotal.service_fee)}</span>
                      </div>
                    </div>
                    <hr style={{ border: 'none', borderTop: '2px solid #e5e7eb', margin: '24px 0' }} />
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '32px' }}>
                      <span style={{ fontSize: '20px', fontWeight: 'bold', color: '#1f2937' }}>Total</span>
                      <span style={{ fontSize: '24px', fontWeight: 'bold', color: '#1f2937' }}>${formatCurrency(cartTotal.total)}</span>
                    </div>
                    <button
                      onClick={nextStep}
                      style={{
                        width: '100%',
                        padding: '16px 24px',
                        backgroundColor: '#1f2937',
                        color: 'white',
                        border: 'none',
                        borderRadius: '12px',
                        fontSize: '16px',
                        fontWeight: '600',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '8px',
                        boxShadow: '0 4px 12px rgba(31, 41, 55, 0.15)'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.backgroundColor = '#374151';
                        e.target.style.transform = 'translateY(-2px)';
                        e.target.style.boxShadow = '0 6px 16px rgba(31, 41, 55, 0.2)';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.backgroundColor = '#1f2937';
                        e.target.style.transform = 'translateY(0)';
                        e.target.style.boxShadow = '0 4px 12px rgba(31, 41, 55, 0.15)';
                      }}
                    >
                      Continuar con el Envío
                      <i className="fas fa-arrow-right"></i>
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Step 2: Shipping */}
        {currentStep === 2 && (
          <div style={{ display: 'flex', gap: '28px', flexWrap: 'wrap' }}>
            <div style={{ flex: '1 1 550px' }}>
              <div style={{
                backgroundColor: 'white',
                borderRadius: '12px',
                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                marginBottom: '24px'
              }}>
                <div style={{
                  padding: '24px',
                  borderBottom: '1px solid #e5e7eb',
                  backgroundColor: '#f9fafb',
                  borderRadius: '12px 12px 0 0'
                }}>
                  <h4 style={{ margin: 0, fontSize: '20px', fontWeight: 'bold', color: '#1f2937' }}>
                    Información de Envío
                  </h4>
                </div>
                <div style={{ padding: '24px' }}>
                  <div className="shipping-form" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '20px' }}>
                    <div>
                      <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500', color: '#374151' }}>
                        Nombre completo
                      </label>
                      <InputIcon
                        type="text"
                        value={fullName}
                        onChange={(e) => setFullName(e.target.value)}
                        placeholder="Ingresa tu nombre completo"
                        startIcon={User}
                        required
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500', color: '#374151' }}>
                        Dirección de Email
                      </label>
                      <InputIcon
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="Ingresa tu email"
                        startIcon={Mail}
                        required
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500', color: '#374151' }}>
                        Número de Teléfono
                      </label>
                      <PhoneInput
                        value={mobile}
                        onChange={setMobile}
                        defaultCountry="PY"
                        style={{
                          width: '100%',
                        }}
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500', color: '#374151' }}>
                        País
                      </label>
                      <CountrySelector
                        value="PY"
                        onChange={setCountry}
                      />
                    </div>
                    <div style={{ gridColumn: '1 / -1' }}>
                      <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500', color: '#374151' }}>
                        Dirección de Envío
                      </label>
                      <InputIcon
                        type="text"
                        value={address}
                        onChange={(e) => setAddress(e.target.value)}
                        placeholder="Ingresa tu dirección"
                        startIcon={MapPin}
                        required
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500', color: '#374151' }}>
                        Ciudad
                      </label>
                      <InputIcon
                        type="text"
                        value={city}
                        onChange={(e) => setCity(e.target.value)}
                        placeholder="Ingresa tu ciudad"
                        startIcon={Building}
                        required
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500', color: '#374151' }}>
                        Estado/Provincia
                      </label>
                      <InputIcon
                        type="text"
                        value={state}
                        onChange={(e) => setState(e.target.value)}
                        placeholder="Ingresa tu estado/provincia"
                        startIcon={Map}
                        required
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="order-summary" style={{ flex: '0 0 300px' }}>
              <div style={{
                backgroundColor: 'white',
                borderRadius: '12px',
                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                position: 'sticky',
                top: '20px'
              }}>
                <div style={{
                  padding: '24px',
                  borderBottom: '1px solid #e5e7eb',
                  backgroundColor: '#f9fafb',
                  borderRadius: '12px 12px 0 0'
                }}>
                  <h5 style={{ margin: 0, fontSize: '18px', fontWeight: 'bold', color: '#1f2937' }}>
                    Resumen del Pedido
                  </h5>
                </div>
                <div style={{ padding: '24px' }}>
                  <div style={{ marginBottom: '20px' }}>
                    {cart.map(item => (
                      <div key={item.id} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                        <span style={{ fontSize: '14px', color: '#6b7280' }}>
                          {item.product.title} x {item.qty}
                        </span>
                        <span style={{ fontSize: '14px', fontWeight: '500' }}>
                          ${formatCurrency(item.sub_total)}
                        </span>
                      </div>
                    ))}
                  </div>
                  <hr style={{ border: 'none', borderTop: '1px solid #e5e7eb', margin: '20px 0' }} />
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '20px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ color: '#6b7280' }}>Subtotal</span>
                      <span style={{ fontWeight: '500' }}>${formatCurrency(cartTotal.sub_total)}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ color: '#6b7280' }}>Envío</span>
                      <span style={{ fontWeight: '500' }}>${formatCurrency(cartTotal.shipping)}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ color: '#6b7280' }}>Impuesto</span>
                      <span style={{ fontWeight: '500' }}>${formatCurrency(cartTotal.tax)}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ color: '#6b7280' }}>Tarifa de Servicio</span>
                      <span style={{ fontWeight: '500' }}>${formatCurrency(cartTotal.service_fee)}</span>
                    </div>
                  </div>
                  <hr style={{ border: 'none', borderTop: '1px solid #e5e7eb', margin: '20px 0' }} />
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '24px' }}>
                    <span style={{ fontSize: '18px', fontWeight: 'bold', color: '#1f2937' }}>Total</span>
                    <span style={{ fontSize: '18px', fontWeight: 'bold', color: '#1f2937' }}>${formatCurrency(cartTotal.total)}</span>
                  </div>
                  <div style={{ display: 'flex', gap: '12px' }}>
                    <button 
                      onClick={prevStep}
                      style={{
                        flex: 1,
                        padding: '12px 16px',
                        backgroundColor: 'transparent',
                        color: '#6b7280',
                        border: '1px solid #d1d5db',
                        borderRadius: '8px',
                        fontSize: '14px',
                        fontWeight: '600',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease'
                      }}
                    >
                      <i className="fas fa-arrow-left me-2"></i> Paso Anterior
                    </button>
                    <button 
                      onClick={nextStep}
                      disabled={!fullName || !email || !mobile || !address || !city || !state || !country}
                      style={{
                        flex: 1,
                        padding: '12px 16px',
                        backgroundColor: (!fullName || !email || !mobile || !address || !city || !state || !country) ? '#9ca3af' : '#1f2937',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        fontSize: '14px',
                        fontWeight: '600',
                        cursor: (!fullName || !email || !mobile || !address || !city || !state || !country) ? 'not-allowed' : 'pointer',
                        transition: 'background-color 0.2s ease'
                      }}
                    >
                      Siguiente Paso <i className="fas fa-arrow-right ms-2"></i>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Step 3: Payment */}
        {currentStep === 3 && (
          <div style={{ display: 'flex', gap: '28px', flexWrap: 'wrap' }}>
            <div style={{ flex: '1 1 550px' }}>
              <div style={{
                backgroundColor: 'white',
                borderRadius: '12px',
                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                marginBottom: '24px'
              }}>
                <div style={{
                  padding: '24px',
                  borderBottom: '1px solid #e5e7eb',
                  backgroundColor: '#f9fafb',
                  borderRadius: '12px 12px 0 0'
                }}>
                  <h4 style={{ margin: 0, fontSize: '20px', fontWeight: 'bold', color: '#1f2937' }}>
                    Información de Pago
                  </h4>
                </div>
                <div style={{ padding: '24px' }}>
                  <div style={{
                    backgroundColor: '#dbeafe',
                    border: '1px solid #93c5fd',
                    borderRadius: '8px',
                    padding: '16px',
                    marginBottom: '24px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px'
                  }}>
                    <i className="fas fa-info-circle" style={{ color: '#1d4ed8' }}></i>
                    <span style={{ color: '#1e40af', fontSize: '14px' }}>
                      Pago seguro con redireccionamiento
                    </span>
                  </div>
                  
                  <div style={{ marginBottom: '24px' }}>
                    <h5 style={{ fontSize: '16px', fontWeight: '600', color: '#1f2937', marginBottom: '12px' }}>
                      Detalles de Envío
                    </h5>
                    <div style={{
                      backgroundColor: '#f9fafb',
                      padding: '16px',
                      borderRadius: '8px',
                      border: '1px solid #e5e7eb'
                    }}>
                      <p style={{ margin: '0 0 4px 0', fontWeight: '600', color: '#1f2937' }}>{fullName}</p>
                      <p style={{ margin: '0 0 4px 0', color: '#6b7280' }}>{email}</p>
                      <p style={{ margin: '0 0 4px 0', color: '#6b7280' }}>{mobile}</p>
                      <p style={{ margin: '0', color: '#6b7280' }}>{address}, {city}, {state}, {country}</p>
                    </div>
                  </div>

                  <div>
                    <h5 style={{ fontSize: '16px', fontWeight: '600', color: '#1f2937', marginBottom: '12px' }}>
                      Resumen del Pedido
                    </h5>
                    <div style={{ overflowX: 'auto' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                          <tr style={{ backgroundColor: '#f9fafb' }}>
                            <th style={{ padding: '12px', textAlign: 'left', fontSize: '14px', fontWeight: '600', color: '#374151' }}>
                              Productos
                            </th>
                            <th style={{ padding: '12px', textAlign: 'center', fontSize: '14px', fontWeight: '600', color: '#374151' }}>
                              Cantidad
                            </th>
                            <th style={{ padding: '12px', textAlign: 'right', fontSize: '14px', fontWeight: '600', color: '#374151' }}>
                              Precio
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {cart.map(item => (
                            <tr key={item.id} style={{ borderBottom: '1px solid #e5e7eb' }}>
                              <td style={{ padding: '12px', fontSize: '14px', color: '#1f2937' }}>
                                {item.product.title}
                              </td>
                              <td style={{ padding: '12px', textAlign: 'center', fontSize: '14px', color: '#6b7280' }}>
                                {item.qty}
                              </td>
                              <td style={{ padding: '12px', textAlign: 'right', fontSize: '14px', fontWeight: '500', color: '#1f2937' }}>
                                ${formatCurrency(item.sub_total)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <div style={{ flex: '0 0 300px' }}>
              <div style={{
                backgroundColor: 'white',
                borderRadius: '12px',
                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                position: 'sticky',
                top: '20px'
              }}>
                <div style={{
                  padding: '24px',
                  borderBottom: '1px solid #e5e7eb',
                  backgroundColor: '#f9fafb',
                  borderRadius: '12px 12px 0 0'
                }}>
                  <h5 style={{ margin: 0, fontSize: '18px', fontWeight: 'bold', color: '#1f2937' }}>
                    Total del Pedido
                  </h5>
                </div>
                <div style={{ padding: '24px' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '20px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ color: '#6b7280' }}>Subtotal</span>
                      <span style={{ fontWeight: '500' }}>${formatCurrency(cartTotal.sub_total)}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ color: '#6b7280' }}>Envío</span>
                      <span style={{ fontWeight: '500' }}>${formatCurrency(cartTotal.shipping)}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ color: '#6b7280' }}>Impuesto</span>
                      <span style={{ fontWeight: '500' }}>${formatCurrency(cartTotal.tax)}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ color: '#6b7280' }}>Tarifa de Servicio</span>
                      <span style={{ fontWeight: '500' }}>${formatCurrency(cartTotal.service_fee)}</span>
                    </div>
                  </div>
                  <hr style={{ border: 'none', borderTop: '1px solid #e5e7eb', margin: '20px 0' }} />
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '24px' }}>
                    <span style={{ fontSize: '18px', fontWeight: 'bold', color: '#1f2937' }}>Total</span>
                    <span style={{ fontSize: '18px', fontWeight: 'bold', color: '#1f2937' }}>${formatCurrency(cartTotal.total)}</span>
                  </div>
                  <div className="payment-buttons" style={{ display: 'flex', gap: '12px' }}>
                    <button 
                      onClick={prevStep}
                      style={{
                        flex: 1,
                        padding: '12px 16px',
                        backgroundColor: 'transparent',
                        color: '#6b7280',
                        border: '1px solid #d1d5db',
                        borderRadius: '8px',
                        fontSize: '14px',
                        fontWeight: '600',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease'
                      }}
                    >
                      <i className="fas fa-arrow-left me-2"></i> Paso Anterior
                    </button>
                    
                    <button 
                      onClick={sendWhatsAppMessageWithOrder}
                      style={{
                        width: '100%',
                        padding: '14px 20px',
                        backgroundColor: '#25d366',
                        color: 'white',
                        border: 'none',
                        borderRadius: '10px',
                        fontSize: '16px',
                        fontWeight: '700',
                        cursor: 'pointer',
                        transition: 'all 0.3s ease',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '10px',
                        boxShadow: '0 4px 12px rgba(37, 211, 102, 0.3)',
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.backgroundColor = '#128c7e';
                        e.target.style.transform = 'translateY(-2px)';
                        e.target.style.boxShadow = '0 6px 16px rgba(37, 211, 102, 0.4)';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.backgroundColor = '#25d366';
                        e.target.style.transform = 'translateY(0)';
                        e.target.style.boxShadow = '0 4px 12px rgba(37, 211, 102, 0.3)';
                      }}
                    >
                      <i className="fab fa-whatsapp" style={{ fontSize: '20px' }}></i> Finalizar Pedido via WhatsApp
                    </button>
                    
                    {/* Stripe Payment Button - Commented out, only WhatsApp checkout available
                    <button 
                      onClick={createOrder}
                      disabled={loading}
                      style={{
                        width: '100%',
                        padding: '12px 16px',
                        backgroundColor: loading ? '#9ca3af' : '#1f2937',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        fontSize: '14px',
                        fontWeight: '600',
                        cursor: loading ? 'not-allowed' : 'pointer',
                        transition: 'background-color 0.2s ease',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '8px'
                      }}
                    >
                      {loading ? (
                        <>
                          <div style={{
                            width: '16px',
                            height: '16px',
                            border: '2px solid transparent',
                            borderTop: '2px solid white',
                            borderRadius: '50%',
                            animation: 'spin 1s linear infinite'
                          }}></div>
                          Procesando...
                        </>
                      ) : (
                        <>
                          <i className="fas fa-credit-card"></i> Realizar Pedido
                        </>
                      )}
                    </button>
                    */}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Responsive Styles */}
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        
        /* Global mobile overflow prevention */
        @media (max-width: 768px) {
          body {
            overflow-x: hidden !important;
          }
          
          * {
            max-width: 100% !important;
            box-sizing: border-box !important;
          }
        }
        
        @media (max-width: 768px) {
          .container {
            padding: 0 8px !important;
            max-width: 100vw !important;
            overflow-x: hidden !important;
          }
          
          .cart-items {
            padding: 8px !important;
            max-width: 100% !important;
            overflow-x: hidden !important;
          }
          
          .cart-items > div {
            flex-direction: column !important;
            gap: 12px !important;
            padding: 12px !important;
            max-width: 100% !important;
            box-sizing: border-box !important;
          }
          
          .cart-items img {
            width: 70px !important;
            height: 70px !important;
            align-self: center !important;
            object-fit: cover !important;
          }
          
          /* Step Navigation Mobile */
          .step-navigation {
            flex-direction: column !important;
            gap: 8px !important;
            padding: 16px !important;
          }
          
          .step-navigation button {
            padding: 10px 16px !important;
            font-size: 14px !important;
            width: 100% !important;
            justify-content: center !important;
          }
          
          /* Order Summary Mobile */
          .order-summary {
            position: static !important;
            width: 100% !important;
            margin-bottom: 20px !important;
          }
          
          /* Shipping Form Mobile */
          .shipping-form {
            grid-template-columns: 1fr !important;
            gap: 16px !important;
          }
          
          /* Cart Item Layout Mobile */
          .cart-item {
            flex-direction: column !important;
            gap: 12px !important;
            padding: 12px !important;
          }
          
          .cart-item > div:first-child {
            align-self: center !important;
          }
          
          .cart-item img {
            width: 80px !important;
            height: 80px !important;
          }
          
          .cart-item-content {
            text-align: center !important;
            width: 100% !important;
            max-width: 100% !important;
            overflow-wrap: break-word !important;
            padding-right: 0 !important;
          }
          
          .cart-item-content h5 {
            font-size: 14px !important;
            margin-bottom: 6px !important;
            line-height: 1.3 !important;
            word-wrap: break-word !important;
          }
          
          .cart-item-content span {
            font-size: 11px !important;
            display: block !important;
            margin-bottom: 2px !important;
            word-wrap: break-word !important;
          }
          
          .cart-item-actions {
            flex-direction: column !important;
            align-items: center !important;
            gap: 8px !important;
            width: 100% !important;
          }
          
          /* Quantity Controls Mobile */
          .quantity-controls {
            justify-content: center !important;
            gap: 8px !important;
            flex-wrap: nowrap !important;
          }
          
          .quantity-controls button {
            width: 32px !important;
            height: 32px !important;
            font-size: 12px !important;
            min-width: 32px !important;
          }
          
          .quantity-controls input {
            width: 60px !important;
            height: 32px !important;
            font-size: 12px !important;
            text-align: center !important;
          }
          
          /* Buttons Mobile */
          button {
            padding: 12px 20px !important;
            font-size: 14px !important;
            min-height: 44px !important; /* Better touch targets */
          }
          
          /* Form Inputs Mobile */
          input, select {
            padding: 12px !important;
            font-size: 16px !important; /* Prevents zoom on iOS */
            min-height: 44px !important; /* Better touch targets */
          }
          
          /* Cart Item Mobile Enhancements */
          .cart-item {
            border-radius: 8px !important;
            margin-bottom: 12px !important;
            box-shadow: 0 2px 4px rgba(0,0,0,0.1) !important;
            width: 100% !important;
            max-width: 100% !important;
            box-sizing: border-box !important;
            overflow: hidden !important;
          }
          
          /* Remove Button Mobile */
          .cart-item button[style*="border: 1px solid #ef4444"] {
            padding: 8px 16px !important;
            font-size: 12px !important;
            width: auto !important;
            margin-top: 8px !important;
            max-width: none !important;
          }
          
          /* Item Total Mobile */
          .cart-item-actions h6 {
            font-size: 16px !important;
            font-weight: bold !important;
            color: #1f2937 !important;
            margin: 0 !important;
            text-align: center !important;
          }
          
          /* Payment Buttons Mobile */
          .payment-buttons {
            display: flex !important;
            flex-direction: column !important;
            gap: 12px !important;
          }
          
          .payment-buttons button {
            width: 100% !important;
            padding: 14px 20px !important;
            font-size: 16px !important;
            font-weight: 600 !important;
          }
          
          /* WhatsApp Button Mobile */
          button[style*="background-color: #25d366"] {
            background-color: #25d366 !important;
            color: white !important;
            border: none !important;
            border-radius: 8px !important;
            margin-bottom: 12px !important;
          }
          
          /* Order Summary Mobile */
          .order-summary > div {
            border-radius: 8px !important;
            box-shadow: 0 2px 8px rgba(0,0,0,0.1) !important;
          }
          
          /* Table Mobile */
          table {
            font-size: 14px !important;
          }
          
          table th, table td {
            padding: 8px !important;
          }
        }
        
        @media (max-width: 480px) {
          .container {
            padding: 0 8px !important;
          }
          
          .cart-items {
            padding: 8px !important;
          }
          
          .cart-items > div {
            padding: 12px !important;
          }
          
          .cart-items img {
            width: 60px !important;
            height: 60px !important;
          }
          
          /* Step Navigation Small Mobile */
          .step-navigation button {
            padding: 6px 10px !important;
            font-size: 11px !important;
          }
          
          /* Buttons Small Mobile */
          button {
            padding: 8px 12px !important;
            font-size: 13px !important;
          }
          
          /* Form Inputs Small Mobile */
          input, select {
            padding: 8px !important;
            font-size: 14px !important;
          }
        }
      `}</style>
    </div>
  );
}

export default Cart;

